import type { Usuario } from './types';

const BASE = '/api/v1';

/** Erro de API com o status HTTP e a mensagem em português vinda do servidor. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: { campo: string; mensagem: string }[],
  ) {
    super(message);
  }
}

/** Texto único para mostrar ao usuário: mensagem do servidor + detalhes de validação, se houver. */
export function mensagemDoErro(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.details?.length) return `${e.message}: ${e.details.map((d) => (d.campo ? `${d.campo} — ${d.mensagem}` : d.mensagem)).join('; ')}`;
    return e.message;
  }
  return 'Não foi possível concluir. Verifique a conexão e tente novamente.';
}

// O access token vive só na memória (nunca em localStorage): um XSS não consegue levá-lo embora.
// A sessão longa é o cookie HttpOnly de refresh, que o JavaScript nem enxerga.
let accessToken: string | null = null;
let aoPerderSessao: () => void = () => {};
// Inspeção ("ver como"): o token é de outra conta, curto e NÃO se renova. Se der 401, não podemos renovar (a renovação devolveria
// a sessão do administrador e a tela passaria a mostrar os dados dele achando que são do inspecionado): avisamos e a tela volta ao administrador.
let emInspecao = false;
let aoExpirarInspecao: () => void = () => {};

export const definirAoPerderSessao = (fn: () => void) => (aoPerderSessao = fn);
export const definirAoExpirarInspecao = (fn: () => void) => (aoExpirarInspecao = fn);
export const temToken = () => accessToken !== null;

/** Passa a usar o token de inspeção no lugar do token do administrador. */
export function usarTokenDeInspecao(token: string) {
  accessToken = token;
  emInspecao = true;
}
/** Larga o token de inspeção; quem chama pede a sessão do administrador de novo (renovarSessao). */
export function largarTokenDeInspecao() {
  accessToken = null;
  emInspecao = false;
}

async function lerErro(res: Response): Promise<ApiError> {
  let corpo: { error?: string; details?: { campo: string; mensagem: string }[] } = {};
  try {
    corpo = await res.json();
  } catch {
    /* corpo vazio ou não-JSON */
  }
  return new ApiError(res.status, corpo.error ?? `Erro ${res.status}`, corpo.details);
}

type Sessao = { accessToken: string; usuario: Usuario };

// Várias chamadas podem receber 401 ao mesmo tempo; só uma renova, as outras esperam o mesmo resultado.
let renovando: Promise<Sessao | null> | null = null;

/** A última renovação falhou por falta de conexão (e não porque a sessão acabou)? Decide se o app de campo segue em modo offline. */
let ultimaFalhaDeRede = false;
export const falhouPorRede = () => ultimaFalhaDeRede;

export function renovarSessao(): Promise<Sessao | null> {
  renovando ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, { method: 'POST' });
      ultimaFalhaDeRede = false;
      if (!res.ok) return null;
      const s = (await res.json()) as Sessao;
      accessToken = s.accessToken;
      return s;
    } catch {
      ultimaFalhaDeRede = true;
      return null;
    } finally {
      renovando = null;
    }
  })();
  return renovando;
}

type Opcoes = {
  method?: string;
  /** Corpo JSON. */
  body?: unknown;
  /** Corpo multipart (upload). O navegador define o Content-Type com o boundary. */
  form?: FormData;
  signal?: AbortSignal;
};

/** Faz a chamada com o token, renovando a sessão uma vez se vier 401. Devolve a Response já validada (2xx). */
async function chamar(caminho: string, op: Opcoes, jaTentouRenovar = false): Promise<Response> {
  const res = await fetch(BASE + caminho, {
    method: op.method ?? 'GET',
    signal: op.signal,
    headers: {
      ...(op.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    body: op.form ?? (op.body !== undefined ? JSON.stringify(op.body) : undefined),
  });

  if (res.status === 401 && emInspecao && !caminho.startsWith('/auth/')) {
    aoExpirarInspecao();
    throw new ApiError(401, 'A inspeção terminou (tempo esgotado). Você voltou para a sua conta.');
  }
  if (res.status === 401 && !jaTentouRenovar && !caminho.startsWith('/auth/')) {
    const s = await renovarSessao();
    if (s) return chamar(caminho, op, true);
    accessToken = null;
    aoPerderSessao();
  }
  if (!res.ok) throw await lerErro(res);
  return res;
}

export async function api<T>(caminho: string, op: Opcoes = {}): Promise<T> {
  const res = await chamar(caminho, op);
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/** Baixa um arquivo protegido (precisa do token, por isso não dá para usar um simples <a href>). */
export async function baixarBlob(caminho: string): Promise<Blob> {
  return (await chamar(caminho, {})).blob();
}

/** Entrega o arquivo ao usuário (diálogo de salvar). */
export function salvarBlob(blob: Blob, nome: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function entrar(email: string, senha: string): Promise<Usuario> {
  const s = await api<Sessao>('/auth/login', { method: 'POST', body: { email, senha } });
  accessToken = s.accessToken;
  return s.usuario;
}

export async function sair(): Promise<void> {
  try {
    await api('/auth/logout', { method: 'POST' });
  } finally {
    accessToken = null;
  }
}

export const trocarSenha = (senhaAtual: string, novaSenha: string) =>
  api<void>('/auth/trocar-senha', { method: 'POST', body: { senhaAtual, novaSenha } });

/** Monta a query string ignorando valores vazios. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}
